import makeStyles from '@mui/styles/makeStyles'
import { Theme } from '@mui/material'

type StylesType = {
  middleMsg: boolean
  flip: boolean
  startGroup: boolean
  emojiCount: number
}

const borderRadius = '0.705rem'

const getMessageFontSize = ({ emojiCount }: StylesType, theme: Theme) => {
  const base = theme.size.base

  if (emojiCount === 0 || emojiCount >= 5) {
    return base
  }

  if (emojiCount === 1) {
    return `calc(3 * ${base})`
  }

  if (emojiCount === 2) {
    return `calc(2 * ${base})`
  }

  if ([3, 4].includes(emojiCount)) {
    return `calc(1.5 * ${base})`
  }
}

const useStyles = makeStyles<Theme, StylesType>(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    margin: ({ middleMsg }) => (middleMsg ? '0 1.25rem' : '0 1.25rem'),
    fontSize: theme.size.sm
  },

  row: {
    display: 'flex',
    flexDirection: ({ flip }) => (flip ? 'row-reverse' : 'row')
  },

  bottomRow: {
    marginTop: '-4px',
    marginLeft: ({ flip }) => (flip ? '0' : '58px'),
    marginRight: ({ flip }) => (flip ? '58px' : '0')
  },

  channels: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: ({ flip }) => (flip ? 'flex-end' : 'flex-start'),
    marginLeft: ({ flip }) => (flip ? '0' : '2px'),
    marginRight: ({ flip }) => (flip ? '2px' : '0'),
    fontSize: theme.size.sm,
    fontStyle: 'italic',
    color: '#7F8487',
    paddingBottom: '0.3125rem',

    '& > div': {
      display: 'flex',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap'
    },

    '& .channel': {
      marginLeft: '0.125rem',
      alignItems: 'center'
    },

    '& .name': {
      marginLeft: '0.125rem',

      [theme.breakpoints.down('md')]: {
        display: 'none'
      }
    }
  },

  avatar: {
    margin: '0 10px',
    width: '2rem',
    height: '2rem',
    textTransform: 'capitalize',
    fontSize: theme.size.sm,
    padding: '0.125rem',
    '& > img ': {
      borderRadius: '100%'
    },
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },

  messageBase: {
    maxWidth: '75%',

    // Full width on mobile
    [theme.breakpoints.down(767)]: {
      maxWidth: '100%'
    },

    '& .box': {
      border: '2px solid',
      borderRadius,
      boxSizing: 'border-box',
      padding: '0.35rem',
      wordWrap: 'anywhere',
      fontSize: props => getMessageFontSize(props, theme)
    },

    '& .media-box': {},

    '& .box > img': {
      marginTop: '0.5rem',
      width: '100%',
      height: 'auto'
    },

    '& .header': {
      display: 'flex',
      padding: '0.3125rem',
      fontSize: theme.size.sm,
      color: '#7F8487',
      [theme.breakpoints.down('md')]: {
        padding: '0',
        paddingBottom: '0.3125rem'
      }
    },

    '& .header .info': {
      display: 'flex',
      alignItems: 'center',
      paddingTop: 3,
      flexFlow: ({ flip }) => flip && 'row-reverse'
    },

    '& .header .info .name': {
      margin: '0px 8px',
      marginRight: ({ flip }) => (flip ? 0 : 8),
      marginLeft: ({ flip }) => (flip ? 8 : 0),
      color: '#1F1F1F',
      fontSize: theme.size.base,
      cursor: 'default',

      [theme.breakpoints.down('md')]: {
        display: 'none'
      }
    },

    '& .header .info .rest': {
      display: 'flex',
      alignItems: 'center',
      cursor: 'default',

      '& > div': {
        display: 'flex',
        alignItems: 'center'
      },

      '& svg': {
        width: 14,
        height: 14
      }
    }
  },

  fullWidth: {
    maxWidth: '100%'
  },
  sentMethod: {
    display: 'grid',
    gridTemplateColumns: 'minmax(20px,auto) auto auto',
    alignItems: 'center',
    margin: '0 2.5px',
    '& svg': {
      flexShrink: 0,
      margin: '0px 5px'
    }
  },

  sentTime: {
    margin: '0px 2.5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  message: {
    '& .box': {
      borderColor: ({ flip }) => (flip ? theme.palette.primary.main : 'white'),
      borderRadius: ({ flip }) =>
        flip ? '16px 0px 16px 16px' : '0px 16px 16px 16px',
      backgroundColor: ({ flip }) =>
        flip ? theme.palette.primary.main : 'white',
      color: ({ flip }) => (flip ? 'white' : 'black')
    },

    '& .footer': {
      flexDirection: ({ flip }) => (flip ? 'row-reverse' : 'row')
    }
  },

  note: {
    '& .box': {
      borderColor: theme.palette.secondary.main,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.primary.main
    },

    '& .footer': {
      flexDirection: 'row-reverse'
    }
  },
  onlyImages: {
    '& .box': {
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      color: theme.palette.primary.main
    }
  },

  icon: {
    marginRight: '5px'
  },

  dots: {
    margin: ({ flip }) => (flip ? '0.5rem 0.25rem 0 0' : '0.5rem 0 0 0.25rem'),
    height: 'max-content',
    width: 'max-content',

    '& .dot': {
      fontSize: theme.size.lg,
      cursor: 'pointer',

      '&:hover': {
        color: theme.palette.primary.main
      }
    }
  },

  seen: {
    color: '#7F8487',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  topTick: {
    position: 'absolute',
    marginLeft: 10,
    [theme.breakpoints.down('md')]: {
      marginLeft: 5
    }
  },

  bottomTick: {
    position: 'relative'
  },

  errorMsg: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '2px',
    margin: '0.25rem 0',
    color: '#7F8487',
    alignItems: 'center'
  },

  retry: {
    cursor: 'pointer'
  },

  msgContent: {
    whiteSpace: 'break-spaces',

    '& span': {
      margin: '0 1px',
      padding: '0.15rem 0.5rem',
      borderRadius: borderRadius
    }
  },

  middleMsg: {
    marginTop: '7px !important'
  },

  start: {
    marginBottom: ({ startGroup }) => startGroup && '0'
  },

  end: {
    marginTop: 7
  },

  edited: {
    marginLeft: '0.25rem',
    fontSize: theme.size.sm,
    fontStyle: 'italic',
    opacity: '0.4'
  },

  mention: {
    cursor: 'default',
    background: '#f8d8ff',
    color: theme.palette.primary.main,
    padding: '4px !important',
    borderRadius: '2px !important'
  },

  mentionYou: {
    cursor: 'default',
    background: '#f8d8ff',
    color: theme.palette.primary.main,
    padding: '4px !important',
    borderRadius: '2px !important'
  },

  hasContentAndMedia: {
    marginBottom: 10
  },

  mediaRow: {
    marginTop: '-4px',

    '&:not(:last-child)': {
      marginBottom: 10
    },

    [theme.breakpoints.down('md')]: {
      marginTop: '0px'
    }
  }
}))

export default useStyles
